import { useCallback, useEffect, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../component/LoadingSpinner";
import moment from "moment-timezone";
import { serviceFleetGetFleetHistory } from "../../api/serviceFleetMaster";

const ITEMS_PER_PAGE = 50;

function nowForInput() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

function toUtcISOString(datetimeLocalValue) {
  const dt = new Date(datetimeLocalValue); // interpreted local
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString();
}

function localDisplay(utcTimestamp) {
  return moment.utc(utcTimestamp).local().format("MMM D, YYYY, h:mm:ss A");
}

const flattenForCsv = (row) => ({
  ...row,
  // _id_timestamp: row?._id?.timestamp ?? '',
  //_id_counter: row?._id?.counter ?? '',
  // _id_randomValue1: row?._id?.randomValue1 ?? '',
  // _id_randomValue2: row?._id?.randomValue2 ?? '',
  // _id: undefined,  // remove original _id object
  _id: row?._id?.timestamp ?? "",
});

const escapeCsvCell = (value) => {
  if (value === null || value === undefined) return "";

  value = String(value).replace(/"/g, '""');

  return /[",\n\r]/.test(value) ? `"${value}"` : value;
};

const escapeCsvCellss = (value) => {
  if (value === null || value === undefined) return "";

  // Convert objects/arrays to JSON so CSV gets readable text instead of [object Object]
  if (typeof value === "object") {
    try {
      value = JSON.stringify(value);
    } catch {
      value = String(value);
    }
  } else {
    value = String(value);
  }

  // Escape quotes by doubling them
  value = value.replace(/"/g, '""');

  // Wrap in quotes if it contains CSV special chars
  if (/[",\n\r]/.test(value)) {
    value = `"${value}"`;
  }

  return value;
};

export default function HistoryRegoTable({
  vehicle,
  controlPanelOpen,
  setControlPanelOpen,
}) {
  const navigate = useNavigate();

  const [fleetData, setFleetData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [rowFilter, setRowFilter] = useState("");
  const [page, setPage] = useState(1);

  // default to last 7 days
  useEffect(() => {
    const now = new Date();
    const seven = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    setStartDate(seven.toISOString().slice(0, 16));
    setEndDate(nowForInput());
  }, []);

  const serverfetchFleetHistory = useCallback(
    async ({ vehicleId, startLocal, endLocal }) => {
      const startIso = toUtcISOString(startLocal); // 2026-10-07T04:55:00.000Z
      const endIso = toUtcISOString(endLocal); // 2026-10-14T04:55:00.000Z

      if (!startIso || !endIso) {
        toast.error("Please enter valid start & end dates.");
        return false;
      }

      if (new Date(startIso) > new Date(endIso)) {
        toast.error("Start date must be older than end date.");
        return false;
      }

      try {
        setLoading(true);
        setPage(1);

        const sortedData = await serviceFleetGetFleetHistory({
          vehicleId,
          startIso,
          endIso,
        });

        setFleetData(sortedData);

        return sortedData.length > 0;
      } catch (err) {
        toast.error(`Unexpected error: ${err?.message ?? "Unknown error"}`);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // auto fetch when vehicle changes AND dates are ready
  useEffect(() => {
    if (!vehicle) return;
    if (!startDate || !endDate) return; // important: wait until defaults are set
    serverfetchFleetHistory({
      vehicleId: vehicle._id,
      startLocal: startDate,
      endLocal: endDate,
    });
  }, [vehicle, startDate, endDate, serverfetchFleetHistory]);

  const handleSearch = async () => {
    if (!vehicle) {
      toast.error("Select a vehicle first");
      return;
    }

    const hasData = await serverfetchFleetHistory({
      vehicleId: vehicle._id,
      startLocal: startDate,
      endLocal: endDate,
    });
    if (hasData) {
      setControlPanelOpen(false);
    }
  };

  const filtered = useMemo(() => {
    const q = rowFilter.trim().toLowerCase();
    if (!q) return fleetData;
    // return fleetData.filter((item) =>
    //   Object.values(item).join(" ").toLowerCase().includes(q),
    // );
    return fleetData.filter((item) =>
      [
        item.timestamp,
        item.address,
        item.status,
        item.speedKmh,
        item.weights?.total,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [fleetData, rowFilter]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    //useful when filter it will reset to page 1.
    if (page > totalPages) setPage(1);
  }, [totalPages]);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, safePage]);

  const canReplay =
    !!vehicle && !!startDate && !!endDate && fleetData.length > 0;

  const goPlayback = (path) => {
    if (!canReplay) {
      toast.error("Select a vehicle and load history first.");
      return;
    }

    const startIso = toUtcISOString(startDate);
    const endIso = toUtcISOString(endDate);

    // const latLngArray = fleetData.map(({ latitude, longitude }) => [latitude, longitude]);

    // Sort oldest to newest using timestamp
    const sortedFleetData = [...fleetData].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    );

    // const latLngArray = fleetData.map((item) => {
    const latLngArray = sortedFleetData.map((item) => {
      const [lon, lat] = item.location?.coordinates;
      return [lat, lon];
    });

    navigate(path, {
      state: {
        vehicle,
        startDate: startIso,
        endDate: endIso,
        fleetData: sortedFleetData,
        latLngArray,
      },
    });
  };

  const download = (format) => {
    try {
      if (!filtered || filtered.length === 0) return;

      if (format === "json") {
        const blob = new Blob([JSON.stringify(filtered, null, 2)], {
          type: "application/json",
        });
        saveAs(blob, "fleetmaster_history.json");
        return;
      }

      if (format === "csv") {
        const data = filtered.map(flattenForCsv);
        if (data.length === 0) return;

        const headers = Object.keys(data[0]);
        const lines = [
          headers.map(escapeCsvCellss).join(","),
          ...data.map((row) =>
            headers.map((h) => escapeCsvCellss(row?.[h])).join(","),
          ),
        ];

        // Add UTF-8 BOM for Excel compatibility
        const csv = "\uFEFF" + lines.join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        saveAs(blob, "fleetmaster_history.csv");
      }
    } catch {
      toast.error("Failed to download data.");
    }
  };

  //for status online, idle, moving color in table
  const statusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "online":
        return "status-dot online"; //green
      case "paused":
        return "status-dot idle"; //orange
      case "idle":
        return "status-dot idle"; //orange
      case "offline":
      default:
        return "status-dot offline"; //red
    }
  };

  //address lat,lng show
  const latLngText = (item) => {
    const [lon, lat] = item.location?.coordinates || [];
    if (lat == null || lon == null) return "";
    return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
  };

  const openInMaps = (item) => {
    const [lon, lat] = item.location?.coordinates || [];
    if (lat == null || lon == null) return;

    const url = `https://www.google.com/maps?q=${lat},${lon}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderPager = () => {
    const pages = [];
    const max = 3;
    const start = Math.max(1, safePage - Math.floor(max / 2));
    const end = Math.min(totalPages, start + max - 1);

    if (start > 1) pages.push(1, "…");
    for (let p = start; p <= end; p++) pages.push(p);
    if (end < totalPages) pages.push("…", totalPages);

    return (
      <div
        className="fleettable-pager"
        role="navigation"
        aria-label="Pagination"
      >
        <button
          className="fleettable-btn ghost"
          onClick={() => setPage(1)}
          disabled={safePage === 1}
        >
          <i className="bi bi-chevron-double-left" aria-hidden="true" />
          First
        </button>

        <button
          className="fleettable-btn ghost"
          onClick={() => setPage((v) => Math.max(1, v - 1))}
          disabled={safePage === 1}
        >
          <i className="bi bi-chevron-left" aria-hidden="true" />
          Prev
        </button>

        <div className="fleettable-pager-pages">
          {pages.map((p, idx) =>
            p === "…" ? (
              <span key={`e-${idx}`} className="fleettable-ellipsis">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`fleettable-page-btn ${p === safePage ? "is-active" : ""}`}
                onClick={() => setPage(p)}
                disabled={p === safePage}
              >
                {p}
              </button>
            ),
          )}
        </div>

        <button
          className="fleettable-btn ghost"
          onClick={() => setPage((v) => Math.min(totalPages, v + 1))}
          disabled={safePage === totalPages}
        >
          Next
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>

        <button
          className="fleettable-btn ghost"
          onClick={() => setPage(totalPages)}
          disabled={safePage === totalPages}
        >
          Last
          <i className="bi bi-chevron-double-right" aria-hidden="true" />
        </button>
      </div>
    );
  };

  if (!vehicle) {
    return (
      <div className="fleettable-empty-center">
        <div className="fleettable-empty-card fleettable-pop">
          <div className="fleettable-empty-icon">
            <i className="bi bi-truck-front" aria-hidden="true" />
          </div>
          <div className="fleettable-empty-title">Select a vehicle</div>
          <div className="fleettable-empty-sub">
            Select a vehicle from the list, choose a date range and search to
            load history.
          </div>
          <div className="fleettable-empty-hint">
            <i className="bi bi-lightning-charge" aria-hidden="true" />
            Tip: select a vehicle to load the last 7 days
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fleettable-table-wrap">
      {/* Controls */}
      <div
        className={`fleettable-controls-accordion ${controlPanelOpen ? "is-open" : "is-closed"}`}
      >
        <div className="fleettable-controls-accordion-inner">
          <div className="fleettable-controls">
            <div className="fleettable-controls-holder">
              <div className="fleettable-field">
                <label>Start Date</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={nowForInput()}
                />
              </div>
              <div className="fleettable-field">
                <label>End Date</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  max={nowForInput()}
                />
              </div>

              <div className="fleettable-field">
                {/* <div className="fleettable-field grow"> */}
                <label>Filter Rows</label>
                <input
                  type="text"
                  placeholder="Type to filter..."
                  value={rowFilter}
                  onChange={(e) => setRowFilter(e.target.value)}
                />
              </div>
            </div>

            <div className="fleettable-controls-holder">
              <div className="fleettable-field">
                <label>Export</label>
                <select onChange={(e) => download(e.target.value)} value="">
                  <option value="">Download...</option>
                  <option value="json">JSON</option>
                  <option value="csv">CSV</option>
                </select>
              </div>

              <button
                className="fleettable-btn primary"
                onClick={() => goPlayback("/ccontrol-history")}
                disabled={!canReplay}
              >
                <i className="bi bi-play-fill" aria-hidden="true" /> Replay
              </button>
              <button className="fleettable-btn primary" onClick={handleSearch}>
                <i className="bi bi-search" aria-hidden="true" /> Search
              </button>
            </div>

            <button
              type="button"
              className="fleettable-btn-float"
              onClick={() => setControlPanelOpen(false)}
              title="Hide controls"
            >
              <i className="bi bi-eye-slash" aria-hidden="true" />
              Hide
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="fleettable-loading">
          <LoadingSpinner loadingText={"Loading records…"} />
        </div>
      ) : paginated.length === 0 ? (
        <div className="fleettable-no-data fleettable-fade-up">
          <i className="bi bi-inbox" aria-hidden="true" />
          <span>No data available for this range.</span>
        </div>
      ) : (
        <>
          <div className="fleettable-table-scroller history-fade-up">
            <table className="fleettable-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Timestamp</th>
                  <th className="wide">Address</th>
                  <th>Total</th>
                  <th>Steer</th>
                  <th>Drive</th>
                  <th>Trailer A</th>
                  <th>Trailer B</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((item, idx) => (
                  <tr
                    key={item._id}
                    style={{ animationDelay: `${Math.min(idx, 18) * 16}ms` }}
                    className="history-row"
                  >
                    <td>
                      <span
                        className={statusColor(item.status)}
                        title={item.status}
                        aria-label={`Status: ${item.status}`}
                      />
                    </td>
                    <td className="mono">{localDisplay(item.timestamp)}</td>
                    <td
                      className="wide address-cell clickable"
                      title={latLngText(item)}
                      onClick={() => openInMaps(item)}
                    >
                      {item.address || "-"}
                    </td>
                    <td className="strong">{item.weights?.total}</td>
                    <td>{item.weights?.steer}</td>
                    <td>{item.weights?.drive}</td>
                    <td>{item.weights?.trailerA}</td>
                    <td>{item.weights?.trailerB}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="fleettable-footer fleettable-fade-up">
            <div className="fleettable-count">
              <i className="bi bi-list-check" aria-hidden="true" /> {totalItems}{" "}
              items
            </div>
            {renderPager()}
          </div>
        </>
      )}
    </div>
  );
}
